import { useEffect, useRef, useState } from 'react';

export interface PWAState {
  isInstallable: boolean;
  isInstalled: boolean;
  isOnline: boolean;
  isUpdating: boolean;
  hasUpdate: boolean;
}

export interface PWAActions {
  install: () => Promise<void>;
  update: () => void;
}

export function usePWA(): PWAState & PWAActions {
  const [isInstallable, setIsInstallable] = useState(false);
  const [isInstalled, setIsInstalled] = useState(
    () => typeof window !== 'undefined' && window.matchMedia?.('(display-mode: standalone)').matches
  );
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [isUpdating, setIsUpdating] = useState(false);
  const [hasUpdate, setHasUpdate] = useState(false);

  const deferredPromptRef = useRef<BeforeInstallPromptEvent | null>(null);

  // Handle install prompt
  useEffect(() => {
    const handleBeforeInstallPrompt = (e: Event) => {
      e.preventDefault();
      deferredPromptRef.current = e as BeforeInstallPromptEvent;
      setIsInstallable(true);
      try {
        (window as any).deferredPrompt = deferredPromptRef.current;
      } catch (_e) {}
      try {
        const stack = new Error().stack;
        console.debug(
          'PWA beforeinstallprompt intercepted: preventDefault called. deferredPrompt saved on window.deferredPrompt',
          { ts: Date.now(), stack }
        );
      } catch (_e) {
        console.debug('PWA beforeinstallprompt intercepted');
      }
    };

    const handleAppInstalled = () => {
      setIsInstalled(true);
      setIsInstallable(false);
      deferredPromptRef.current = null;
      console.log('PWA was installed');
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', handleAppInstalled);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, []);

  // Handle online/offline status
  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Handle service worker updates
  useEffect(() => {
    if (!('serviceWorker' in navigator) || import.meta.env.DEV) {
      return;
    }

    // Guard: reload exactly once. Without this, a SW that claims/activates
    // repeatedly (or two registrations) can fire controllerchange in a loop and
    // reload the page "a cada rato".
    let refreshing = false;
    const handleControllerChange = () => {
      if (refreshing) return;
      refreshing = true;
      setIsUpdating(false);
      console.log('Service Worker updated, page will reload');
      window.location.reload();
    };

    let registration: ServiceWorkerRegistration | null = null;

    navigator.serviceWorker.ready
      .then((reg) => {
        registration = reg;

        // Check for updates periodically
        const interval = setInterval(() => {
          reg.update().catch((error) => {
            console.error('Service Worker update check failed:', error);
          });
        }, 60000); // Check every minute

        // Check immediately
        reg.update().catch((error) => {
          console.error('Service Worker update check failed:', error);
        });

        // Handle update found
        if (reg.waiting) {
          setHasUpdate(true);
        }

        reg.addEventListener('updatefound', () => {
          const newWorker = reg.installing;
          if (newWorker) {
            newWorker.addEventListener('statechange', () => {
              if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
                setHasUpdate(true);
              }
            });
          }
        });

        navigator.serviceWorker.addEventListener('controllerchange', handleControllerChange);

        return () => {
          clearInterval(interval);
          navigator.serviceWorker.removeEventListener('controllerchange', handleControllerChange);
        };
      })
      .catch((error) => {
        console.error('Service Worker not ready:', error);
      });

    return () => {
      if (registration) {
        registration.removeEventListener('updatefound', () => {});
      }
    };
  }, []);

  const install = async () => {
    if (!deferredPromptRef.current) {
      throw new Error('Install prompt not available');
    }

    deferredPromptRef.current.prompt();
    const { outcome } = await deferredPromptRef.current.userChoice;
    console.log(`User response to install prompt: ${outcome}`);
    deferredPromptRef.current = null;
  };

  const update = () => {
    if (!hasUpdate || import.meta.env.DEV) {
      return;
    }

    setIsUpdating(true);

    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.ready.then((registration) => {
        if (registration.waiting) {
          registration.waiting.postMessage({ type: 'SKIP_WAITING' });
        }
      });
    }
  };

  return {
    isInstallable,
    isInstalled,
    isOnline,
    isUpdating,
    hasUpdate,
    install,
    update,
  };
}

// Type augmentation for BeforeInstallPromptEvent
declare global {
  interface BeforeInstallPromptEvent extends Event {
    prompt: () => Promise<void>;
    userChoice: Promise<{
      outcome: 'accepted' | 'dismissed';
    }>;
  }

  interface Window {
    deferredPrompt?: BeforeInstallPromptEvent;
  }
}
